import React, { useEffect, useRef, useState } from 'react';
import { string } from 'prop-types';

/**
 * Counts a figure up when it first scrolls into view.
 *
 * Takes the finished display string — "11+", "100%", "4.2×" — rather than a
 * number plus formatting props, so the copy stays in `en.json` and this
 * component never has to know how a figure is punctuated.
 *
 * Anything it cannot parse is rendered verbatim, so a value like "6 wks" or
 * "Q3" degrades to static text instead of breaking.
 */

const DURATION_MS = 1300;

/** Splits "−38%" into { prefix: '−', value: 38, suffix: '%', decimals: 0 }. */
const parse = display => {
  const match = /^(\D*?)(\d+(?:\.\d+)?)(.*)$/.exec(String(display).trim());
  if (!match) return null;

  const [, prefix, digits, suffix] = match;
  const decimals = digits.includes('.') ? digits.split('.')[1].length : 0;
  return { prefix, value: Number(digits), suffix, decimals };
};

const CountUp = ({ value, className }) => {
  const parsed = parse(value);
  const ref = useRef(null);

  // Start at the final value: if anything below bails out, the figure is
  // already correct rather than stuck on zero.
  const [shown, setShown] = useState(value);

  useEffect(() => {
    if (!parsed) return undefined;

    const node = ref.current;
    if (!node || typeof IntersectionObserver === 'undefined') return undefined;

    const prefersReduced =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return undefined;

    const { prefix, value: target, suffix, decimals } = parsed;
    let frame = null;
    let done = false;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting || done) return;
          done = true;
          observer.unobserve(entry.target);

          let start = null;
          const step = now => {
            if (start === null) start = now;
            const progress = Math.min((now - start) / DURATION_MS, 1);
            // Ease-out cubic: fast first, settling on the number.
            const eased = 1 - Math.pow(1 - progress, 3);
            setShown(`${prefix}${(target * eased).toFixed(decimals)}${suffix}`);
            if (progress < 1) frame = requestAnimationFrame(step);
          };

          // Only blank to zero once we know the animation will actually run.
          setShown(`${prefix}${(0).toFixed(decimals)}${suffix}`);
          frame = requestAnimationFrame(step);
        });
      },
      { threshold: 0.6 }
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
    // `value` is the only input; `parsed` is derived from it.
  }, [value]);

  return (
    <span ref={ref} className={className}>
      {shown}
    </span>
  );
};

CountUp.propTypes = {
  value: string.isRequired,
  className: string,
};

export default CountUp;
